import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, IconButton, Paper, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, Collapse, Link, Tabs, Tab, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAdminFetch } from '../hooks/useAdminFetch';

interface SentryIssue {
  id: string;
  shortId: string;
  title: string;
  culprit: string;
  permalink: string;
  firstSeen: string;
  lastSeen: string;
  count: string;
  userCount: number;
  level: string;
  status: string;
  platform: string;
  project: {
    id: string;
    name: string;
    slug: string;
  };
  metadata: {
    type?: string;
    value?: string;
    filename?: string;
    function?: string;
  };
}

interface SentryEvent {
  id: string;
  eventID: string;
  dateCreated: string;
  message: string;
  platform: string;
  entries: Array<{
    type: string;
    data: any;
  }>;
  tags: Array<{ key: string; value: string }>;
  context?: Record<string, any>;
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
}

const AdminErrorTracking: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [issues, setIssues] = useState<SentryIssue[]>([]);
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
  const [issueEvents, setIssueEvents] = useState<Record<string, SentryEvent[]>>({});
  const [eventsLoading, setEventsLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = unresolved, 1 = resolved, 2 = ignored
  const [updating, setUpdating] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchIssues = async (query: string = 'is:unresolved') => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/sentry/issues/?query=${encodeURIComponent(query)}`);
      if (data.configured === false) {
        setConfigured(false);
        setIssues([]);
      } else {
        setConfigured(true);
        setIssues(data.issues || []);
      }
    } catch (err: any) {
      // Don't show error if redirected for 2FA
      if (!err.message?.includes('Admin authentication')) {
        setConfigured(true);
        setError(err.message || 'Failed to fetch issues');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queries = ['is:unresolved', 'is:resolved', 'is:ignored'];
    fetchIssues(queries[activeTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, apiUrl]);

  const handleRefresh = () => {
    const queries = ['is:unresolved', 'is:resolved', 'is:ignored'];
    fetchIssues(queries[activeTab]);
  };

  const handleExpandIssue = async (issueId: string) => {
    if (expandedIssue === issueId) {
      setExpandedIssue(null);
      return;
    }

    setExpandedIssue(issueId);

    // Fetch events if not already loaded
    if (!issueEvents[issueId]) {
      setEventsLoading(issueId);
      try {
        const events = await adminFetch(`${apiUrl}/api/admin/sentry/issues/${issueId}/events/`);
        setIssueEvents((prev) => ({ ...prev, [issueId]: events }));
      } catch (err) {
        console.error('Failed to fetch events:', err);
      } finally {
        setEventsLoading(null);
      }
    }
  };

  const handleUpdateStatus = async (issueId: string, status: 'resolved' | 'ignored' | 'unresolved') => {
    setUpdating(issueId);
    try {
      await adminFetch(`${apiUrl}/api/admin/sentry/issues/${issueId}/update/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      // Refresh the list
      handleRefresh();
    } catch (err: any) {
      if (!err.message?.includes('Admin authentication')) {
        setError(err.message || 'Failed to update issue');
      }
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getLevelColor = (level: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (level) {
      case 'error':
      case 'fatal':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderStackTrace = (event: SentryEvent) => {
    const exceptionEntry = event.entries?.find((e) => e.type === 'exception');
    if (!exceptionEntry) return null;

    const frames = exceptionEntry.data?.values?.[0]?.stacktrace?.frames || [];
    const reversedFrames = [...frames].reverse().slice(0, 10);

    return (
      <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 1 }}>
        {reversedFrames.map((frame: any, idx: number) => (
          <Box key={idx} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
              {frame.filename}:{frame.lineno} in {frame.function || '(anonymous)'}
            </Typography>
            {frame.context_line && (
              <Typography variant="caption" component="pre" sx={{ color: 'error.main', m: 0, whiteSpace: 'pre-wrap' }}>
                {frame.context_line}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  // Not configured state
  if (!loading && !configured) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Box className="page-header">
          <Box className="title-row" sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">Error Tracking</Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Sentry is not configured. To enable error tracking:
        </Alert>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Setup Instructions</Typography>
          <ol>
            <li>
              <Typography variant="body2" paragraph>
                Create a free account at{' '}
                <Link href="https://sentry.io" target="_blank" rel="noopener noreferrer">
                  sentry.io
                </Link>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                Create a new project (select React for frontend, Django for backend, or create one combined project)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                Add these environment variables to Heroku (backend):
              </Typography>
              <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontSize: '0.8rem', overflow: 'auto' }}>
                {`SENTRY_DSN=https://xxx@xxx.ingest.sentry.io/xxx
                SENTRY_API_TOKEN=your-auth-token
                SENTRY_ORG=your-org-slug
                SENTRY_PROJECT=your-project-slug`}
              </Box>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                Add to Netlify (frontend):
              </Typography>
              <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontSize: '0.8rem', overflow: 'auto' }}>
                {`REACT_APP_SENTRY_DSN=https://xxx@xxx.ingest.sentry.io/xxx`}
              </Box>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                Install the Sentry package in the frontend:
              </Typography>
              <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontSize: '0.8rem' }}>
                npm install @sentry/react
              </Box>
            </li>
          </ol>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Error Tracking</Typography>
        </Box>
        <Box className="page-header-buttons">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Unresolved" />
        <Tab label="Resolved" />
        <Tab label="Ignored" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : issues.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BugReportIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6">No {['unresolved', 'resolved', 'ignored'][activeTab]} issues</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0 ? "Great! Your application is running smoothly." : "No issues in this category."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Error</TableCell>
                <TableCell width={100}>Level</TableCell>
                <TableCell width={80}>Events</TableCell>
                <TableCell width={80}>Users</TableCell>
                <TableCell width={120}>Last Seen</TableCell>
                <TableCell width={150}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue) => (
                <React.Fragment key={issue.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleExpandIssue(issue.id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedIssue === issue.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {issue.metadata?.type || issue.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {issue.culprit}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.level}
                        size="small"
                        color={getLevelColor(issue.level)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{issue.count}</TableCell>
                    <TableCell>{issue.userCount}</TableCell>
                    <TableCell>
                      <Tooltip title={formatDate(issue.lastSeen)}>
                        <span>{formatRelativeTime(issue.lastSeen)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {issue.status !== 'resolved' && (
                          <Tooltip title="Mark Resolved">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUpdateStatus(issue.id, 'resolved')}
                              disabled={updating === issue.id}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {issue.status !== 'ignored' && (
                          <Tooltip title="Ignore">
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateStatus(issue.id, 'ignored')}
                              disabled={updating === issue.id}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {issue.status !== 'unresolved' && (
                          <Tooltip title="Unresolve">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleUpdateStatus(issue.id, 'unresolved')}
                              disabled={updating === issue.id}
                            >
                              <BugReportIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Open in Sentry">
                          <IconButton
                            size="small"
                            component="a"
                            href={issue.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedIssue === issue.id ? undefined : 'none' }}>
                      <Collapse in={expandedIssue === issue.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                          {eventsLoading === issue.id ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : issueEvents[issue.id]?.length ? (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>
                                Latest Event
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="body2" color="error" gutterBottom>
                                  {issueEvents[issue.id][0].message || issue.metadata?.value || issue.title}
                                </Typography>
                                {issueEvents[issue.id][0].user && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    User: {issueEvents[issue.id][0].user?.email || issueEvents[issue.id][0].user?.id || 'Anonymous'}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                  {formatDate(issueEvents[issue.id][0].dateCreated)}
                                </Typography>
                                {renderStackTrace(issueEvents[issue.id][0])}
                              </Paper>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No event details available
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdminErrorTracking;
